import { createContext, useContext, type ReactNode } from 'react';
import type { BlockField, BlockFieldStyle } from '@/blocks/domain/types';
import type { SectionLayout } from '@/blocks/domain/types/BlockContent/HeaderContent';
import { useDocumentSurface } from '@/document-render/presentation/primitives/useDocumentSurface';
import { useOptionalEditableDocument } from './EditableDocumentContext';

/** Where a new element lands: a column of a section, at `index` (0 = top; past the end = append). */
export interface CanvasSlot {
  sectionId: string;
  column: number;
  index: number;
}

export interface CanvasAddOption {
  style: BlockFieldStyle;
  label: string;
  icon: ReactNode;
}

/**
 * The column ⋮ menu's actions (LLAMS-699): the same Duplicate / Delete the Sections panel offers,
 * reachable on the canvas. Absent for a block whose structure is fixed, so no menu is drawn.
 */
export interface CanvasColumnActions {
  /** False once the section already has the maximum 3 columns. */
  canDuplicate: (sectionId: string) => boolean;
  /** False for a section's only column (delete the section instead). */
  canRemove: (sectionId: string) => boolean;
  duplicate: (sectionId: string, column: number) => void;
  /** The host asks first when the column holds elements; an empty one goes straight away. */
  remove: (sectionId: string, column: number) => void;
}

/**
 * The structural edits the block editor canvas makes on its own page (section tab + column
 * switcher, "+ Add element", the insertion line, the element menu, move arrows, inline tools).
 * `blocks/presentation` implements it over the open draft and mounts it with
 * `CanvasStructureProvider`; `document-render` only calls it, so no blocks UI is imported here.
 */
export interface CanvasStructureApi {
  addOptions: CanvasAddOption[];
  insertElement: (style: BlockFieldStyle, slot: CanvasSlot) => void;
  addSection: () => void;
  setSectionLayout: (sectionId: string, layout: SectionLayout) => void;
  canMoveElementBy: (fieldId: string, delta: number) => boolean;
  moveElementBy: (fieldId: string, delta: number) => void;
  canMoveSectionBy: (sectionId: string, delta: number) => boolean;
  moveSectionBy: (sectionId: string, delta: number) => void;
  duplicateElement: (fieldId: string) => void;
  removeElement: (fieldId: string) => void;
  patchElement: (fieldId: string, patch: Partial<BlockField>) => void;
  columnMenu?: CanvasColumnActions;
}

/** The canvas drag in flight, for drop indicators: the hovered target and which side of it. */
export interface CanvasDragState {
  activeId: string | null;
  overId: string | null;
  after: boolean;
}

const IDLE: CanvasDragState = { activeId: null, overId: null, after: false };

const StructureContext = createContext<CanvasStructureApi | null>(null);
const DragStateContext = createContext<CanvasDragState>(IDLE);

export function CanvasStructureProvider({ value, children }: { value: CanvasStructureApi | null; children: ReactNode }) {
  return <StructureContext.Provider value={value}>{children}</StructureContext.Provider>;
}

export function CanvasDragStateProvider({ value, children }: { value: CanvasDragState; children: ReactNode }) {
  return <DragStateContext.Provider value={value}>{children}</DragStateContext.Provider>;
}

/**
 * The structure API for editing chrome, or null wherever that chrome must not appear: no editing
 * provider, the off-screen measurement pass, the export surface (the PDF) or a canvas whose
 * structure is locked (`structureEditable: false`, the quote canvas).
 */
export function useCanvasStructure(): CanvasStructureApi | null {
  const api = useContext(StructureContext);
  const editable = useOptionalEditableDocument();
  const exported = useDocumentSurface() === 'export';
  if (!api || !editable || exported || editable.structureEditable === false) return null;
  return api;
}

export function useCanvasDragState(): CanvasDragState {
  return useContext(DragStateContext);
}
